'use client';

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type Severity = 'error' | 'warning' | 'info' | 'success';

export interface Toast {
  id: string;
  severity: Severity;
  title: string;
  message?: string;
  action?: { label: string; onClick: () => void };
  dedupeKey: string;
  count: number;
  sticky: boolean;
  duration: number;
  createdAt: number;
  exiting: boolean;
  hidden: boolean; // true = collapsed into overflow chip, never rendered directly
}

export interface ToastOptions {
  message?: string;
  action?: { label: string; onClick: () => void };
  dedupeKey?: string;
  sticky?: boolean;
  duration?: number;
}

export const OVERFLOW_ID  = '__overflow__';
export const COALESCE_ID  = '__coalesce__';
const MAX_VISIBLE         = 5;
const RATE_LIMIT          = 10; // max new distinct toasts per RATE_WINDOW_MS
const RATE_WINDOW_MS      = 1000;
const SEV_ORDER: Severity[] = ['error', 'warning', 'info', 'success'];

function highestSeverity(ts: Toast[]): Severity {
  return SEV_ORDER.find(s => ts.some(t => t.severity === s)) ?? 'info';
}

export const DEFAULTS: Record<Severity, { sticky: boolean; duration: number }> = {
  error:   { sticky: true,  duration: 0 },
  warning: { sticky: true,  duration: 0 },
  info:    { sticky: false, duration: 6000 },
  success: { sticky: false, duration: 4000 },
};

interface ToastCtxType {
  toasts: Toast[];
  add: (severity: Severity, title: string, opts?: ToastOptions) => void;
  dismiss: (id: string) => void;
  remove: (id: string) => void;
}

const ToastCtx = createContext<ToastCtxType>({
  toasts: [], add: () => {}, dismiss: () => {}, remove: () => {},
});

// Imperative bridge for non-React contexts (fetch layer, global handlers, SSE)
type AddFn = (severity: Severity, title: string, opts?: ToastOptions) => void;
let _add: AddFn | null = null;
export const toastBus = {
  error:   (title: string, opts?: ToastOptions) => _add?.('error',   title, opts),
  warning: (title: string, opts?: ToastOptions) => _add?.('warning', title, opts),
  info:    (title: string, opts?: ToastOptions) => _add?.('info',    title, opts),
  success: (title: string, opts?: ToastOptions) => _add?.('success', title, opts),
};

// Rate-limit state — module-level so it is never inside a setToasts updater
// (React Strict Mode double-invokes updaters; module-level mutations are not doubled).
let _recentTimestamps: number[] = [];

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const add = useCallback((severity: Severity, title: string, opts: ToastOptions = {}) => {
    const now  = Date.now();
    const def  = DEFAULTS[severity];
    const dedupeKey = opts.dedupeKey ?? `${severity}|${title}|${opts.message ?? ''}`;

    // ── Rate-limit tracking (outside setToasts to avoid Strict Mode double-mutation)
    _recentTimestamps = _recentTimestamps.filter(t => now - t < RATE_WINDOW_MS);
    _recentTimestamps.push(now);
    const isFlooding = _recentTimestamps.length > RATE_LIMIT;

    setToasts(prev => {
      // ── 1. Dedup wins unconditionally (before rate-limit check)
      const existing = prev.find(t => t.dedupeKey === dedupeKey && !t.exiting && !t.hidden);
      if (existing) {
        return [
          ...prev.filter(t => t.id !== existing.id),
          { ...existing, count: existing.count + 1, createdAt: now },
        ];
      }

      // ── 2. Rate-limit: coalesce flood of distinct errors
      if (isFlooding) {
        const existingCoalesce = prev.find(t => t.id === COALESCE_ID && !t.exiting);
        if (existingCoalesce) {
          // Flood already coalescing — just increment the count in the title
          const n = existingCoalesce.count + 1;
          return prev.map(t =>
            t.id === COALESCE_ID ? { ...t, count: n, title: `Multiple errors occurring (${n})` } : t,
          );
        }
        // First coalesced toast: absorb all burst-era individual toasts created in this window
        const burstStart = _recentTimestamps[0] ?? now;
        const toAbsorb   = prev.filter(t =>
          t.id !== COALESCE_ID &&
          t.createdAt >= burstStart - 50, // 50 ms tolerance for clock granularity
        );
        // Count absorbed distinct toasts (exclude the overflow chip, it's a meta-container)
        const absorbedCount = toAbsorb.filter(t => t.id !== OVERFLOW_ID).length;
        const totalCount    = absorbedCount + 1; // +1 for the current call
        const absorbedIds   = new Set(toAbsorb.map(t => t.id));

        return [
          ...prev.filter(t => !absorbedIds.has(t.id)),
          {
            id: COALESCE_ID,
            severity: 'error' as Severity,
            title: `Multiple errors occurring (${totalCount})`,
            message: undefined,
            action: undefined,
            dedupeKey: COALESCE_ID,
            count: totalCount,
            sticky: true,
            duration: 0,
            createdAt: 1, // always sort to top of stack (before all real toasts)
            exiting: false,
            hidden: false,
          },
        ];
      }

      // ── 3. Normal add with cap logic
      const newToast: Toast = {
        id: crypto.randomUUID(),
        severity, title,
        message: opts.message,
        action: opts.action,
        dedupeKey,
        count: 1,
        sticky: opts.sticky ?? def.sticky,
        duration: opts.duration ?? def.duration,
        createdAt: now,
        exiting: false,
        hidden: false,
      };

      const visibleRegular = prev
        .filter(t => !t.exiting && !t.hidden && t.id !== OVERFLOW_ID && t.id !== COALESCE_ID)
        .sort((a, b) => a.createdAt - b.createdAt);

      if (visibleRegular.length < MAX_VISIBLE) {
        return [...prev, newToast];
      }

      // At cap — evict oldest non-sticky first (immediate, no animation)
      const nonSticky = visibleRegular.filter(t => !t.sticky);
      if (nonSticky.length > 0) {
        return [...prev.filter(t => t.id !== nonSticky[0].id), newToast];
      }

      // All sticky — hide oldest regular toast, create / update overflow chip
      const oldest      = visibleRegular[0];
      const alreadyHidden = prev.filter(t => t.hidden);
      const newHidden   = [...alreadyHidden, oldest];
      const chipSev     = highestSeverity(newHidden);

      return [
        ...prev.filter(t => t.id !== oldest.id && t.id !== OVERFLOW_ID),
        { ...oldest, hidden: true },
        {
          id: OVERFLOW_ID,
          severity: chipSev,
          title: `+${newHidden.length} more`,
          message: undefined,
          action: undefined,
          dedupeKey: OVERFLOW_ID,
          count: newHidden.length,
          sticky: true,
          duration: 0,
          createdAt: newHidden[0].createdAt, // sort chip at oldest-hidden position (top of stack)
          exiting: false,
          hidden: false,
        },
        newToast,
      ];
    });
  }, []);

  // dismiss: triggers exit animation — onAnimationEnd in Toast.tsx calls remove()
  const dismiss = useCallback((id: string) => {
    setToasts(prev => {
      const next = prev.map(t => t.id === id ? { ...t, exiting: true } : t);
      // Overflow chip dismissed → immediately clean up all hidden toasts too
      if (id === OVERFLOW_ID) return next.filter(t => !t.hidden);
      return next;
    });
  }, []);

  // remove: called after exit animation completes; also promotes oldest hidden into freed slot
  const remove = useCallback((id: string) => {
    setToasts(prev => {
      const withoutId = prev.filter(t => t.id !== id);

      // Promotion: if an overflow chip exists, promote oldest hidden into the newly freed slot
      const chip = withoutId.find(t => t.id === OVERFLOW_ID && !t.exiting);
      if (!chip) return withoutId;

      const hidden = withoutId.filter(t => t.hidden).sort((a, b) => a.createdAt - b.createdAt);
      if (hidden.length === 0) return withoutId.filter(t => t.id !== OVERFLOW_ID); // chip with no hidden — remove it

      const toPromote = hidden[0];
      const remaining = hidden.slice(1);

      if (remaining.length === 0) {
        // Last hidden promoted — chip gone
        return [
          ...withoutId.filter(t => t.id !== OVERFLOW_ID && t.id !== toPromote.id),
          { ...toPromote, hidden: false },
        ];
      }

      const newChipSev = highestSeverity(remaining);
      return [
        ...withoutId.filter(t => t.id !== OVERFLOW_ID && t.id !== toPromote.id),
        { ...toPromote, hidden: false },
        { ...chip, count: remaining.length, title: `+${remaining.length} more`, severity: newChipSev, createdAt: remaining[0].createdAt },
      ];
    });
  }, []);

  _add = add;

  return (
    <ToastCtx.Provider value={{ toasts, add, dismiss, remove }}>
      {children}
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const { add, dismiss } = useContext(ToastCtx);
  return {
    error:   (title: string, opts?: ToastOptions) => add('error',   title, opts),
    warning: (title: string, opts?: ToastOptions) => add('warning', title, opts),
    info:    (title: string, opts?: ToastOptions) => add('info',    title, opts),
    success: (title: string, opts?: ToastOptions) => add('success', title, opts),
    dismiss,
  };
}

export function useToastState() {
  return useContext(ToastCtx);
}
